import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { body2xl, bodyLg, responsiveStyle } from '@helpers/typography';
import { between, Device, fluid, from, transition, until } from '@netc/style-utils';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { VideoWithControlsStyles } from '@stories/Components/Misc/VideoWithControls/VideoWithControls.styles';
import styled from 'styled-components';
import HeaderStyles from '../Global/Header/Header.styles';
import { SectionIntroStyles } from '@stories/Components/Misc/SectionIntro/SectionIntro.styles';
import { m } from 'framer-motion';
import { verticalFadeInCSS, verticalFadeIn } from '@helpers/variants';
const Container = styled(SectionWrapper) `
  ${SectionWrapperStyles.Inner} {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  @media ${from(Device.TabletLarge)} {
    ${SectionIntroStyles.Container} {
      max-width: 60%;
    }
  }

  @media ${from(Device.DesktopLarge)} {
    ${SectionIntroStyles.Container} {
      max-width: 50%;
    }
  }
`;
const Intro = styled(m.div).attrs(() => ({
    variants: verticalFadeIn(-25),
})) `
  ${verticalFadeInCSS()}
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 24px;

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
  }

  @media ${from(Device.DesktopSmall)} {
    align-items: center;
  }
`;
const Media = styled(m.div).attrs(() => ({
    variants: verticalFadeIn(25, undefined, undefined, undefined, true),
})) `
  ${verticalFadeInCSS()}
  --height: ${fluid(428, 508)};
  --gap: ${fluid(12, 24)};

  &:not([data-layout='four-images']) {
    max-height: var(--height);
    height: var(--height);
  }

  @media ${until(Device.TabletLarge)} {
    display: flex;
    --divisions: calc(100% / ${(props) => props['data-columns']});

    &:not([data-layout='four-images']):not(:has(${() => MediaItem}:only-child)) {
      margin-left: calc(var(--sitePadding) * -1);
      margin-right: calc(var(--sitePadding) * -1);

      &:hover,
      &:focus {
        &:not(:has(${() => MediaItem}:hover)):not(:has(${() => MediaItem}:focus)) {
          ${() => MediaItem}:first-child {
            flex-basis: 70%;
          }
        }

        ${() => MediaItem} {
          flex-basis: calc(30% / ${(props) => props['data-columns'] - 1});

          &:hover {
            flex-basis: 70%;
          }
        }
      }
    }

    ${() => MediaItem} {
      ${transition('flex-basis', 0.25, 'ease-in-out')};
      flex-basis: var(--divisions);

      &:not(:only-child) {
        padding-right: var(--gap);
        &:first-child {
          padding-left: var(--sitePadding);
        }
        &:last-child {
          padding-right: var(--sitePadding);
        }
      }
    }

    // Handle the weird edge cases
    &[data-layout='four-images'] {
      flex-wrap: wrap;
      gap: var(--gap);

      > div {
        padding: unset !important;
        aspect-ratio: 1 / 1;
        flex-basis: calc(50% - var(--gap) / 2);

        @media ${between(Device.Tablet, Device.TabletLarge)} {
          aspect-ratio: 1.5 / 1;
        }
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    display: grid;
    grid-template-columns: repeat(${(props) => props['data-columns']}, 1fr);
    gap: var(--gap);
    width: 100%;

    &[data-layout='two-images-one-video'] {
      grid-template-columns: repeat(5, 1fr);

      > div:nth-child(1),
      > div:nth-child(2) {
        grid-column: span 1;
      }

      > div:nth-child(3) {
        grid-column: span 3;
      }
    }
  }
`;
const MediaItem = styled(m.div).attrs(() => ({
    variants: verticalFadeIn(25),
})) `
  ${verticalFadeInCSS()}
  width: 100%;
  height: auto;
  border-radius: 8px;
  overflow: hidden;

  video,
  img {
    object-fit: cover;
    max-height: 100%;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    overflow: hidden;
    pointer-events: none;
  }

  @media ${until(Device.TabletLarge)} {
    &:not(:only-child) {
      &:has(video) {
        &:not(&:hover) {
          ${VideoWithControlsStyles.Controls} {
            opacity: 0;
          }
        }
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    height: 100%;
  }
`;
const IntroRight = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${fluid(16, 24)};

  > p[data-intro] {
    ${responsiveStyle(bodyLg, body2xl)};

    strong {
      ${fonts.montserrat.bold}
    }
  }

  @media ${from(Device.Tablet)} {
    max-width: min(60%, 726px);
    width: 100%;
    justify-content: flex-end;
  }

  @media ${from(Device.DesktopSmall)} {
    margin-top: auto;
  }

  ${HeaderStyles.HeaderTextbox} {
    --bgColour: ${brand.global.white};
    --iconSize: 24px;
    --paddingX: 24px;
    border: 1px solid ${brand.ngbBlue.default};
    height: 56px;
    width: 100%;
    max-width: calc(100% - calc(var(--paddingX) * 2 + var(--iconSize) / 2) - var(--paddingX));
  }
`;
const Links = styled.div `
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${fluid(8, 12)};

  > a {
    ${bodyLg}
    ${fonts.montserrat.medium};
    color: ${brand.ngbBlue.default};
  }

  > div[data-spacer] {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${brand.ngbBlue.default};
  }
`;
export const InnerHeroBannerStyles = {
    Container,
    Intro,
    Media,
    MediaItem,
    IntroRight,
    Links,
};
